import { onMounted, onUnmounted, ref, ComputedRef } from 'vue'
import { EXTERNAL_LINKS } from '@/scripts/app-configs/constants'
import { segment } from '@/utils/segment'
import { EventKeysType } from '@/utils/segment/segment-events'

export enum IframeMessageEventType {
  OPEN_SUMMARY_PATIENT = 'open:summary-patient',
  READY = 'ready',
  UPDATE_URL = 'update:url',
  UPDATE_ONLY_REPLY_TO_MEMBER = 'update:only-reply-to-member',
  UPDATE_MARK_TASK_AS_DONE = 'update:mark-task-as-done',
  UPDATE_MEMBER_ACCOUNT_UUID = 'update:member-account-uuid',
  TRIGGER_SEGMENT_EVENT = 'trigger:segment-event',
}

interface IframeMessageBase<T extends IframeMessageEventType, P = undefined> {
  type: T
  payload?: P
}

export type IframeMessage =
  IframeMessageBase<IframeMessageEventType.UPDATE_ONLY_REPLY_TO_MEMBER, { isOnlyReplyToMember: boolean }>
  | IframeMessageBase<IframeMessageEventType.UPDATE_URL, { url: string, replace?: boolean }>
  | IframeMessageBase<IframeMessageEventType.OPEN_SUMMARY_PATIENT, { patientId: string }>
  | IframeMessageBase<IframeMessageEventType.READY>
  | IframeMessageBase<IframeMessageEventType.UPDATE_MARK_TASK_AS_DONE>
  | IframeMessageBase<IframeMessageEventType.UPDATE_MEMBER_ACCOUNT_UUID, { memberAccountUuid: string }>
  | IframeMessageBase<IframeMessageEventType.TRIGGER_SEGMENT_EVENT, { event: EventKeysType, properties?: Record<string, unknown> }>

export type UseIframeCommunicationCallback = (message: IframeMessage) => void

export const iFrameOrigins = Object.freeze({
  FEED: 'feed',
  CHAT_POPUP: 'chat-popup',
  CHAT_PAGE: 'chat-page',
} as const)

export type iFrameOrigin = typeof iFrameOrigins[keyof typeof iFrameOrigins]

interface UseIframeCommunicationOptions {
  filterOrigin?: boolean
  segmentProperties?: ComputedRef<Partial<Record<EventKeysType, Record<string, unknown>>>>
  iFrameOrigin?: iFrameOrigin
}

/**
 * Composable for handling communication with an embedded iframe via postMessage.
 * Listens for messages and provides helpers to send messages to the iframe.
 *
 * @param callback Optional callback to handle incoming iframe messages
 * @param options  Options for filtering messages (e.g., by origin).
 */
export default function useIframeCommunication(
  callback?: UseIframeCommunicationCallback,
  options: UseIframeCommunicationOptions = { filterOrigin: true },
) {
  const uiPortalIFrameIsReady = ref(false)

  const handleMessageEvent = (event: MessageEvent<IframeMessage>) => {
    const { origin, data } = event

    if (options.filterOrigin && event.origin !== EXTERNAL_LINKS.uiPortal.url) return

    if (origin === EXTERNAL_LINKS.uiPortal.url && data.type === IframeMessageEventType.READY) {
      uiPortalIFrameIsReady.value = true
    }

    if (data.type === IframeMessageEventType.TRIGGER_SEGMENT_EVENT && data.payload) {
      const eventSpecificProps = { ...options.segmentProperties?.value?.[data.payload.event], origin: options.iFrameOrigin }

      segment.trackEventWithUser(data.payload.event, {
        ...(data.payload.properties || {}),
        ...eventSpecificProps,
      })
    }

    if (callback) {
      callback(data)

      return
    }
  }

  const postMessageToIframe = (iframe: HTMLIFrameElement | null, message: IframeMessage) => {
    if (!iframe || !iframe.contentWindow) return

    iframe.contentWindow.postMessage(
      message,
      EXTERNAL_LINKS.uiPortal.url,
    )
  }

  /**
   * Sends an update route url message to the iframe.
   * @param iframe The target iframe element.
   * @param url    The URL to send to the iframe.
   */
  const updateIframeRoute = (iframe: HTMLIFrameElement | null, url: string) => {
    postMessageToIframe(
      iframe,
      {
        type: IframeMessageEventType.UPDATE_URL,
        payload: { url },
      },
    )
  }

  /**
   * Sends an update only-reply-to-member message to the iframe.
   * @param iframe               The target iframe element.
   * @param isOnlyReplyToMember  If onlyReplyToMember is active
   */
  const updateIframeIsOnlyReplyToMember = (iframe: HTMLIFrameElement | null, isOnlyReplyToMember = false) => {
    postMessageToIframe(
      iframe,
      {
        type: IframeMessageEventType.UPDATE_ONLY_REPLY_TO_MEMBER,
        payload: { isOnlyReplyToMember },
      },
    )
  }

  onMounted(() => {
    window.addEventListener('message', handleMessageEvent)
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleMessageEvent)
  })

  return {
    updateIframeRoute,
    updateIframeIsOnlyReplyToMember,
    uiPortalIFrameIsReady,
  }
}
